"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { HStack, Stack, Text } from "@leabankab/component-library";

interface WalletCreditCardProps {
	cardNumber?: string;
	cvv?: string;
	expirationDate?: string;
	width: number;
	height: number;
	hidden: boolean;
	loading?: boolean;
}

const formatCardNumber = (cardNumber?: string) =>
	cardNumber
		?.replace(/\s/g, "")
		.match(/.{1,4}/g)
		?.join(" ") ?? "**** **** **** ****";

export const WalletCreditCard = ({
	cardNumber,
	cvv,
	expirationDate,
	width,
	height,
	hidden,
	loading,
}: WalletCreditCardProps) => {
	const t = useTranslations("pages.AddToWalletPage");
	const cardNumberGroups = formatCardNumber(cardNumber).split(" ");
	const blur = (amount: "blur-sm" | "blur-xs") => (hidden ? amount : "");

	return (
		<div
			className={`relative flex w-full flex-col overflow-hidden rounded-3xl shadow-xl ${hidden ? "brightness-[0.96] saturate-[0.85]" : ""}`}
			style={{ maxWidth: width }}
		>
			<Image
				alt="Lea Bank credit card"
				src="/creditcard.png"
				width={width}
				height={height}
				style={{
					display: "block",
					width: "100%",
					height: "auto",
				}}
			/>
			<Stack
				gap={1.5}
				justify="end"
				className="absolute inset-0 -translate-x-1 p-2.5 text-white sm:p-3.5"
			>
				<HStack gap={1.5} className="w-full">
					{cardNumberGroups.map((group, index) => (
						<Text
							render={<span />}
							key={index}
							color="inherit"
							fontWeight="bold"
							className={`font-mono tracking-[1.5px] whitespace-nowrap ${blur("blur-sm")}`}
						>
							{group}
						</Text>
					))}
				</HStack>
				<HStack gap={3}>
					<Stack gap={0.5}>
						<Text render={<span />} color="inherit">
							{t("validThru")}
						</Text>
						<Text
							render={<span />}
							textStyle="body-sm"
							color="inherit"
							className={`font-mono ${blur("blur-xs")}`}
						>
							{expirationDate ?? "**/**"}
						</Text>
					</Stack>
					<Stack gap={0.5}>
						<Text render={<span />} color="inherit">
							{t("cvv")}
						</Text>
						<Text
							render={<span />}
							textStyle="body-sm"
							color="inherit"
							className={`font-mono ${blur("blur-xs")}`}
						>
							{cvv ?? "***"}
						</Text>
					</Stack>
				</HStack>
			</Stack>
			{loading && (
				<div className="absolute inset-0 flex items-center justify-center bg-black/25">
					<span
						aria-hidden="true"
						className="size-4 animate-spin rounded-full border-[3px] border-white border-t-transparent motion-reduce:animate-none"
					/>
				</div>
			)}
		</div>
	);
};
